import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Announcement } from '../../../types/api/announcements.types';
import {
  emptyGroup,
  flattenSelection,
  groupsFromAnnouncement,
  membersMissingMedia,
  storedMediaKeys,
  type AudienceGroup,
} from '../helpers/announcementAudience';

/**
 * The audience half of the wizard: the groups, their edits, and the flat
 * target list they add up to.
 *
 * Separate from the rest of the form because it is the one part with its own
 * shape - a list of groups of chips - and the modal only ever hands its
 * pieces to `AnnouncementAudienceStep` and reads the selection back.
 */
export const useAudienceGroups = (
  announcement: Announcement | null,
  isOpen: boolean
) => {
  const [groups, setGroups] = useState<AudienceGroup[]>([]);

  // Group ids are local to one editing session; they never reach the API.
  const nextId = useRef(1);
  const generateId = useCallback(() => nextId.current++, []);

  // Rebuilt on every open, so a cancelled edit cannot leak its groups into
  // the next announcement someone creates. A new announcement starts empty:
  // presets were three cards to read and edit before writing anything, and
  // every announcement targets a different audience anyway.
  useEffect(() => {
    if (!isOpen) return;

    nextId.current = 1;
    setGroups(
      announcement ? groupsFromAnnouncement(announcement, generateId) : []
    );
  }, [isOpen, announcement, generateId]);

  const patchGroup = useCallback(
    (id: number, patch: Partial<AudienceGroup>) =>
      setGroups((prev) =>
        prev.map((group) => (group.id === id ? { ...group, ...patch } : group))
      ),
    []
  );

  const toggleChip = useCallback(
    (id: number, index: number) =>
      setGroups((prev) =>
        prev.map((group) =>
          group.id === id
            ? {
                ...group,
                chips: group.chips.map((chip, i) =>
                  i === index ? { ...chip, on: !chip.on } : chip
                ),
              }
            : group
        )
      ),
    []
  );

  const addGroup = useCallback(
    () => setGroups((prev) => [...prev, emptyGroup(generateId())]),
    [generateId]
  );

  const removeGroup = useCallback(
    (id: number) =>
      setGroups((prev) => prev.filter((group) => group.id !== id)),
    []
  );

  const selection = useMemo(() => flattenSelection(groups), [groups]);

  // What the server already holds an image or GIF for, which is the half of
  // the "every target needs one" rule the form cannot see in its own state.
  const storedMedia = useMemo(
    () => storedMediaKeys(announcement),
    [announcement]
  );

  const missingMedia = useMemo(
    () => membersMissingMedia(selection, storedMedia),
    [selection, storedMedia]
  );

  /** Group ids with at least one member still short of an image or GIF. */
  const groupsMissingMedia = useMemo(
    () => new Set(missingMedia.map((member) => member.group.id)),
    [missingMedia]
  );

  return {
    groups,
    selection,
    storedMedia,
    missingMedia,
    groupsMissingMedia,
    patchGroup,
    toggleChip,
    addGroup,
    removeGroup,
  };
};
